import { Link } from 'react-router-dom'
import type { Project } from '../api/types'
import { deadlineLabel, formatDate } from '../lib/format'
import { workSiteLine } from '../lib/projectLabel'
import { progressColor, progressTextColor } from '../lib/progressColor'
import { servicePillStyle } from './ServiceDevicesCard'
import { Avatar, PriorityPill, ProgressBar, StatusPill } from './ui'

/** Projektkort til brugerpanel og projektlisten. Fremhæver næste handling og afventer-årsag. */
export function ProjectCard({ project }: { project: Project }) {
  const isOverdue =
    project.deadline !== null &&
    project.deadline !== undefined &&
    new Date(project.deadline) < new Date() &&
    !project.status.isCompleted

  const site = workSiteLine(project)

  return (
    <Link to={`/projects/${project.id}`} className="card" style={{ display: 'block' }}>
      <div className="row row--between" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          {/* Øverst på kortet, så en servicesag kan kendes i listen uden at læse
              kortet igennem (beslutning 076). En almindelig sag får intet mærke. */}
          {project.kind === 'service' && (
            <span className="pill" style={{ ...servicePillStyle, marginBottom: 6 }}>
              🔧 Servicesag
            </span>
          )}
          <h3 style={{ fontSize: 16 }}>{project.name}</h3>
          {project.customer && <div className="small muted">{project.customer}</div>}
          {/* Sagens egen adresse med fed: hos en kunde med mange sager er det
              adressen, der skiller dem ad (beslutning 077). */}
          {site && (
            <div className="small" style={{ fontWeight: 700 }}>
              {site}
            </div>
          )}
          {project.orderNumber && <div className="tiny muted">Ordre {project.orderNumber}</div>}
        </div>
        <Avatar user={project.projectManager} title={project.projectManager?.displayName ?? 'Ingen projektleder'} />
      </div>

      <div className="row" style={{ marginTop: 10, flexWrap: 'wrap' }}>
        <StatusPill status={project.status} />
        <PriorityPill priority={project.priority} />
        {/* En arkiveret sag skal kunne kendes under "Vis arkiv" (beslutning 090). */}
        {project.isArchived && (
          <span className="pill" style={{ background: '#F3F4F6', color: '#374151' }}>
            Arkiveret
          </span>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <div className="row row--between tiny muted" style={{ marginBottom: 4 }}>
          <span>
            Fremdrift {project.completedTaskCount}/{project.taskCount} opgaver
          </span>
          <span style={{ color: progressTextColor(project.progressPercent), fontWeight: 700 }}>
            {project.progressPercent}%
          </span>
        </div>
        {/* Rød/gul/grøn som på TV-skærmen (Simon 2026-10-28). */}
        <ProgressBar value={project.progressPercent} color={progressColor(project.progressPercent)} />
      </div>

      {project.status.isWaiting && project.waitingReason && (
        <div className="small warn" style={{ marginTop: 10 }}>
          Afventer: {project.waitingReason}
        </div>
      )}

      {project.nextActionText && (
        <div style={{ marginTop: 10 }}>
          <div className="tiny muted">Næste handling</div>
          <div className="small" style={{ fontWeight: 600 }}>
            {project.nextActionText}
          </div>
          <div className="tiny muted">
            {project.nextActionUser?.displayName ?? 'Ingen ansvarlig'}
            {project.nextActionDeadline ? ` · ${formatDate(project.nextActionDeadline)}` : ''}
          </div>
        </div>
      )}

      <div className="row row--between tiny" style={{ marginTop: 12 }}>
        <span className={isOverdue ? 'overdue' : 'muted'}>
          {project.deadline ? `Deadline ${formatDate(project.deadline)} · ${deadlineLabel(project.deadline)}` : 'Ingen deadline'}
        </span>
        {project.overdueTaskCount > 0 && <span className="overdue">{project.overdueTaskCount} forsinket</span>}
      </div>
    </Link>
  )
}
